import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '@/contexts/LanguageContext';

const PILLARS_SL = [
  {
    key: 'tobacco',
    icon: '🛡',
    title: 'TOBACCO-READY',
    color: '#C87533',
    points: [
      { label: 'JTI izkušnje',  desc: '100 % operativna skladnost v regulirani industriji.' },
      { label: 'Zero Risk',      desc: 'Poznavanje ZOUTBP in RSP standardov.' },
      { label: 'Plug-and-Play', desc: 'Takojšen operativni zagon brez uvajanja.' },
    ],
  },
  {
    key: 'elite',
    icon: '⭐',
    title: 'ELITE KADER',
    color: '#C87533',
    points: [
      { label: 'Profil 21+ (25+)', desc: 'Svetovalci, ne promotorji.' },
      { label: 'Stabilnost',        desc: '20 % nižja fluktuacija od tržnega povprečja.' },
      { label: 'Sinergija',         desc: 'HP (edukacija) + Union (prodaja) — preverjena metodologija.' },
    ],
  },
  {
    key: 'agility',
    icon: '⚡',
    title: 'TURNKEY AGILNOST',
    color: '#C87533',
    points: [
      { label: 'End-to-End',      desc: 'Lastna logistika in skladišče na Letališki 34.' },
      { label: 'Direktni nadzor', desc: 'Projekt vodi direktor osebno.' },
      { label: 'Hitrost',          desc: 'Reševanje terenskih izzivov v urah, ne dneh.' },
    ],
  },
];

const PILLARS_EN = [
  {
    key: 'tobacco',
    icon: '🛡',
    title: 'TOBACCO-READY',
    color: '#C87533',
    points: [
      { label: 'JTI Experience',  desc: '100% operational compliance in a regulated industry.' },
      { label: 'Zero Risk',        desc: 'In-depth knowledge of ZOUTBP and RSP standards.' },
      { label: 'Plug-and-Play',   desc: 'Immediate operational launch — no onboarding delay.' },
    ],
  },
  {
    key: 'elite',
    icon: '⭐',
    title: 'ELITE STAFF',
    color: '#C87533',
    points: [
      { label: 'Profile 21+ (25+)', desc: 'Advisors, not promoters.' },
      { label: 'Stability',          desc: '20% lower turnover than market average.' },
      { label: 'Synergy',            desc: 'HP (education) + Union (sales) — proven methodology.' },
    ],
  },
  {
    key: 'agility',
    icon: '⚡',
    title: 'TURNKEY AGILITY',
    color: '#C87533',
    points: [
      { label: 'End-to-End',       desc: 'Own logistics and warehouse at Letališka 34.' },
      { label: 'Direct oversight', desc: 'Project led by the director personally.' },
      { label: 'Speed',             desc: 'Field issues resolved within hours, not days.' },
    ],
  },
];

export const WhyWibeSection = () => {
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const PILLARS = lang === 'sl' ? PILLARS_SL : PILLARS_EN;

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      gsap.fromTo('.ww-header',
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, duration: 0.7, scrollTrigger: { trigger: sectionRef.current, start: 'top 70%', once: true } }
      );
      gsap.fromTo('.ww-card',
        { opacity: 0, y: 36 },
        {
          opacity: 1, y: 0, duration: 0.65, ease: 'power3.out', stagger: 0.12,
          scrollTrigger: { trigger: '.ww-grid', start: 'top 78%', once: true },
        }
      );
      gsap.fromTo('.ww-tagline',
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: 0.6, scrollTrigger: { trigger: '.ww-tagline', start: 'top 90%', once: true } }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="why-wibe"
      className="scroll-section min-h-screen flex items-center px-4 py-20"
    >
      <div className="section-content max-w-[1100px] mx-auto w-full">

        {/* Header */}
        <div className="ww-header text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-[#C87533]/30 bg-[#C87533]/8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C87533]" />
            <span className="font-mono text-xs text-[#C87533] uppercase tracking-widest">
              {lang === 'sl' ? 'Naša prednost' : 'Our Advantage'}
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            {lang === 'sl'
              ? <>Zakaj <span className="text-[#C87533]">Wibe Solutions?</span></>
              : <>Why <span className="text-[#C87533]">Wibe Solutions?</span></>
            }
          </h2>
          <p className="text-white/40 max-w-xl mx-auto text-base leading-relaxed">
            {lang === 'sl'
              ? 'Tri razlogi, zakaj smo pravi partner za INFORMERS 2026.'
              : 'Three reasons why we are the right partner for INFORMERS 2026.'}
          </p>
        </div>

        {/* 3-column grid */}
        <div className="ww-grid grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {PILLARS.map((pillar) => (
            <div
              key={pillar.key}
              className="ww-card group relative rounded-2xl border border-white/8 bg-white/3 backdrop-blur-sm p-6 flex flex-col gap-5 hover:border-white/15 hover:bg-white/5 transition-all duration-300"
            >
              {/* Top accent line */}
              <div
                className="absolute top-0 left-6 right-6 h-px rounded-full"
                style={{ background: `linear-gradient(90deg, ${pillar.color}70, transparent)` }}
              />

              {/* Hover glow */}
              <div
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: `radial-gradient(ellipse 80% 50% at 50% 0%, ${pillar.color}10 0%, transparent 70%)` }}
              />

              {/* Title */}
              <div className="relative flex items-center gap-3">
                <h3
                  className="font-mono text-sm font-bold tracking-widest uppercase"
                  style={{ color: pillar.color }}
                >
                  {pillar.title}
                </h3>
              </div>

              {/* Points */}
              <div className="relative flex flex-col gap-4">
                {pillar.points.map((pt, i) => (
                  <div key={i} className="flex gap-3">
                    <span
                      className="w-1 h-1 rounded-full flex-shrink-0 mt-2"
                      style={{ background: pillar.color }}
                    />
                    <div>
                      <span className="text-white/80 text-sm font-semibold">{pt.label}: </span>
                      <span className="text-white/45 text-sm leading-relaxed">{pt.desc}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom hover line */}
              <div
                className="absolute bottom-0 left-1/2 -translate-x-1/2 h-px w-0 group-hover:w-2/3 transition-all duration-500 rounded-full"
                style={{ background: pillar.color }}
              />
            </div>
          ))}
        </div>

        {/* Tagline */}
        <div className="ww-tagline relative rounded-2xl border border-[#C87533]/25 bg-[#C87533]/5 backdrop-blur-sm px-8 py-5 text-center overflow-hidden">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 60% 80% at 50% 50%, rgba(200,117,51,0.07) 0%, transparent 70%)' }}
          />
          <p className="relative font-display text-base sm:text-lg font-semibold text-white/80 leading-relaxed">
            <span className="text-[#C87533] font-bold">Wibe Solutions: </span>
            {lang === 'sl'
              ? 'Tobačna disciplina s prodajno prodornostjo.'
              : 'Tobacco discipline with sales drive.'}
          </p>
        </div>

      </div>
    </section>
  );
};
